<template>
  <UiContainer class="orders-page">
    <div class="orders-page__header">
      <h1 class="orders-page__title">Мои заказы</h1>
      <p class="orders-page__subtitle">История ваших заказов, включая сохраненные черновики</p>
    </div>

    <div v-if="authLoading" class="orders-page__state">
      <UiProgress variant="spinner" />
      <p>Проверка авторизации...</p>
    </div>

    <UiCard v-else-if="!isAuthenticated" class="orders-page__state orders-page__state--card">
      <span class="orders-page__state-icon" aria-hidden="true">!</span>
      <h2 class="orders-page__state-title">Необходима авторизация</h2>
      <p class="orders-page__state-text">Для просмотра заказов необходимо войти в систему</p>
      <UiButton to="/login">Войти в систему</UiButton>
    </UiCard>

    <template v-else>
      <div class="orders-page__filters">
        <div>
          <label class="orders-page__filter-label">Статус</label>
          <UiSelect
            v-model="filterStatus"
            :options="statusOptions"
            @update:modelValue="applyFilters"
          />
        </div>
        <div>
          <label class="orders-page__filter-label">Дата от</label>
          <UiInput
            v-model="filterDateFrom"
            type="date"
            aria-label="Дата от"
            @update:modelValue="applyFilters"
          />
        </div>
        <div>
          <label class="orders-page__filter-label">Дата до</label>
          <UiInput
            v-model="filterDateTo"
            type="date"
            aria-label="Дата до"
            @update:modelValue="applyFilters"
          />
        </div>
      </div>

      <UiAlert v-if="ordersError" type="error" class="orders-page__alert">
        {{ ordersError }}
      </UiAlert>

      <div v-else-if="ordersLoading" class="orders-page__state">
        <UiProgress variant="spinner" />
        <p>Загрузка заказов...</p>
      </div>

      <template v-else-if="orders.length > 0">
        <div v-for="order in orders" :key="order.id" class="orders-page__order">
          <UiCard class="orders-page__order-card" @click="openOrder(order.id)">
            <div class="orders-page__order-head">
              <div class="orders-page__order-head-left">
                <span class="orders-page__order-number"
                  >Заказ № {{ order.order_number || 'Не указан' }}</span
                >
                <UiBadge
                  :variant="
                    orderStore.getOrderStatusColor(order.status) === 'success'
                      ? 'accent'
                      : 'neutral'
                  "
                >
                  {{ order.status_label }}
                </UiBadge>
              </div>
              <div class="orders-page__order-sum">
                {{ formatPriceWithCents(order.final_amount) }}
              </div>
            </div>
            <div class="orders-page__order-meta">
              <span class="orders-page__order-date">{{ formatDate(order.created_at) }}</span>
              <span>Товаров: {{ order.items_count }}</span>
              <p v-if="order.comment" class="orders-page__order-comment">{{ order.comment }}</p>
            </div>
            <div v-if="orderStore.canCancelOrder(order)" class="orders-page__order-actions">
              <UiButton
                variant="secondary"
                @click.stop="cancelOrder(String(order.id), order.status === 'draft')"
              >
                {{ order.status === 'draft' ? 'Удалить' : 'Отменить' }}
              </UiButton>
            </div>
          </UiCard>
        </div>

        <div v-if="pagination && pagination.pages > 1" class="orders-page__pager">
          <UiPager
            :page="currentPage"
            :total-pages="pagination.pages"
            @prev="goPrev"
            @next="goNext"
          />
        </div>
      </template>

      <UiCard v-else class="orders-page__state orders-page__state--card">
        <span class="orders-page__state-icon orders-page__state-icon--muted">📦</span>
        <h2 class="orders-page__state-title">У вас пока нет заказов и черновиков</h2>
        <p class="orders-page__state-text">
          Когда вы сохраните черновик или сделаете первый заказ, он появится здесь
        </p>
        <UiButton to="/catalog">Перейти в каталог</UiButton>
      </UiCard>
    </template>
  </UiContainer>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useOrderHistory } from '@/modules/order/public'
import { useAppNotifications } from '@/modules/common/public'
import { useAuthSession } from '@/modules/auth/public'
import { formatPriceWithCents } from '@/utils/price'
import UiAlert from '@/shared/ui/UiAlert.vue'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import UiInput from '@/shared/ui/UiInput.vue'
import UiPager from '@/shared/ui/UiPager.vue'
import UiProgress from '@/shared/ui/UiProgress.vue'
import UiSelect from '@/shared/ui/UiSelect.vue'

const router = useRouter()
const orderStore = useOrderHistory()
const notificationStore = useAppNotifications()
const authStore = useAuthSession()

const isAuthenticated = computed(() => authStore.isAuthenticated.value)
const orders = computed(() => orderStore.orders.value)
const ordersError = computed(() => orderStore.error.value)
const ordersLoading = computed(() => orderStore.loading.value)

const currentPage = ref(1)
const pagination = ref<{ pages: number } | null>(null)
const authLoading = ref(false)

const filterStatus = ref('')
const filterDateFrom = ref('')
const filterDateTo = ref('')

const statusOptions = [
  { value: '', label: 'Все статусы' },
  { value: 'draft', label: 'Черновик' },
  { value: 'new', label: 'Новый' },
  { value: 'pending', label: 'Ожидает подтверждения' },
  { value: 'confirmed', label: 'Подтверждён' },
  { value: 'processing', label: 'В обработке' },
  { value: 'shipped', label: 'Отправлен' },
  { value: 'delivered', label: 'Доставлен' },
  { value: 'cancelled', label: 'Отменён' },
]

function openOrder(orderId: number | string) {
  router.push(`/orders/${orderId}`)
}

function applyFilters() {
  currentPage.value = 1
  loadOrders()
}

function formatDate(dateString: string): string {
  if (!dateString) return 'Дата не указана'
  try {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return 'Неверная дата'
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'Ошибка даты'
  }
}

async function loadOrders() {
  if (!isAuthenticated.value) return
  const result = await orderStore.fetchOrders(
    currentPage.value,
    10,
    filterStatus.value || undefined,
    filterDateFrom.value || undefined,
    filterDateTo.value || undefined,
  )
  if (result.success && result.pagination) pagination.value = result.pagination
}

function goPrev() {
  if (currentPage.value <= 1) return
  currentPage.value -= 1
  loadOrders()
}

function goNext() {
  if (!pagination.value || currentPage.value >= pagination.value.pages) return
  currentPage.value += 1
  loadOrders()
}

async function cancelOrder(orderId: string, isDraft: boolean) {
  try {
    const result = await orderStore.cancelOrder(orderId)
    if (result.success) {
      notificationStore.showSuccess(isDraft ? 'Черновик удалён' : 'Заказ успешно отменен')
      loadOrders()
    } else {
      notificationStore.showError(
        result.message || (isDraft ? 'Ошибка удаления черновика' : 'Ошибка отмены заказа'),
      )
    }
  } catch {
    notificationStore.showError(isDraft ? 'Ошибка удаления черновика' : 'Ошибка отмены заказа')
  }
}

onMounted(async () => {
  authLoading.value = true
  if (authStore.token.value && !authStore.user.value) {
    try {
      await authStore.fetchProfile()
    } catch {
      // ignore
    }
  }
  authLoading.value = false
  loadOrders()
})
</script>

<style scoped>
.orders-page {
  padding-top: 24px;
  padding-bottom: 48px;
}

.orders-page__header {
  margin-bottom: 24px;
}

.orders-page__title {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 600;
}

.orders-page__subtitle {
  margin: 0;
  color: #586578;
}

.orders-page__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  gap: 16px;
}

.orders-page__state--card {
  text-align: center;
}

.orders-page__state-icon {
  font-size: 48px;
  color: #f59e0b;
}

.orders-page__state-icon--muted {
  color: #94a3b8;
}

.orders-page__state-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
}

.orders-page__state-text {
  margin: 0 0 24px;
  color: #586578;
}

.orders-page__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 20px;
}

.orders-page__filter-label {
  display: block;
  font-size: 12px;
  color: #586578;
  margin-bottom: 6px;
}

.orders-page__alert {
  margin-bottom: 24px;
}

.orders-page__order {
  margin-bottom: 16px;
}

.orders-page__order-card {
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.orders-page__order-card:hover {
  border-color: var(--color-accent);
}

.orders-page__order-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.orders-page__order-head-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.orders-page__order-number {
  font-weight: 600;
}

.orders-page__order-sum {
  font-weight: 600;
  color: #0f172a;
}

.orders-page__order-meta {
  font-size: 14px;
  color: #586578;
  margin-bottom: 12px;
}

.orders-page__order-date {
  display: block;
  margin-bottom: 4px;
}

.orders-page__order-comment {
  margin: 8px 0 0;
  color: #475569;
}

.orders-page__order-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.orders-page__pager {
  margin-top: 24px;
  display: flex;
  justify-content: center;
}
</style>
